/**
 * pipeline-plugin — the `/pipeline` maturation-pipeline surface as a studio-sibling
 * plugin (#160-G — the MVP capstone).
 *
 * The visible maturation-pipeline console: a `ComposableGenomePlugin` mirroring
 * `storePlugin` / `secretsPlugin` — one `component` editor (`console`) routed at
 * `/pipeline`, composed into the ONE studio genome (gate 4 — `/pipeline` resolves
 * to the registered `console` editor). The editor is `React.lazy` so the whole
 * pipeline surface stays in a split chunk OUT of the app entry (gate 8 — entry
 * < 150 KB gz).
 *
 * It ALSO declares one `action/*` genome entity per pipeline command (add / design
 * / flesh-out / groom / reprioritise / thought / burn) — the maturation verbs
 * NAMED genome-natively, a future agent-hook surface. These are `action`-kind
 * entities (an EXISTING `EntityKind`), so gate 4 holds (NO new kind); their
 * EXECUTION lives in the #160-core fluent command core driven by the console UI —
 * there is NO SDK action-runtime to wire them to (the entities are declarative
 * naming, not an execution registration).
 *
 * @module @ubiclouds/v5-studio/pipeline/pipeline-plugin
 */

import { lazy } from 'react';
import { definePlugin } from '@ubiclouds/v5-sdk/plugins';
import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';

/** The pipeline-surface plugin id. */
export const PIPELINE_PLUGIN_ID = 'v5-studio-pipeline';

/** The pipeline command verbs, NAMED as declarative `action/*` genome entities. */
export const PIPELINE_COMMAND_ACTIONS = [
  'add',
  'design',
  'flesh-out',
  'groom',
  'reprioritise',
  'thought',
  'burn',
] as const;

/** The split-chunk pipeline surface (the `/pipeline` route editor). */
const LazyPipelineConsole = lazy(() => import('./PipelineConsole.js'));

/**
 * The pipeline plugin — the `/pipeline` route → the lazy `console` editor, plus one
 * declarative `action/*` entity per command verb. Composed as a SIBLING in
 * `studioPlugins`; adds no new `EntityKind` (the surface lowers onto
 * `component`/`route`, the verbs onto the EXISTING `action` kind — gate 4 holds).
 */
export const pipelinePlugin: ComposableGenomePlugin = PIPELINE_COMMAND_ACTIONS.reduce(
  (b, verb) => b.action(verb),
  definePlugin(PIPELINE_PLUGIN_ID)
    .name('Pipeline')
    .trustTier('core')
    .editor('console', LazyPipelineConsole)
    .route('/pipeline', { routesTo: 'component/console', title: 'Pipeline' }),
).build();
